import { Suspense, useCallback, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { lazyWithReload } from '@/shared/lib/lazy-with-reload';
import { useVaptAiAvailable } from '../hooks/useVaptAiAvailable';
import styles from './VaptAiLauncher.module.css';

/* ==========================================================================
   Launcher do Vapt IA

   Botão fixo em todas as telas autenticadas, para quem tem a flag e a
   permissão. Ele é a âncora do foco: quem abre pelo teclado volta para cá
   quando a gaveta fecha.

   A gaveta em si entra por `lazyWithReload` — ela carrega a conversa
   inteira, com lista, cards, compositor e cliente de stream, e não faz
   sentido pesar no primeiro carregamento do painel de quem nunca vai
   abri-la. O wrapper, e não o `lazy` do React, porque uma aba aberta durante
   um deploy pede um chunk que já não existe; o wrapper recarrega uma vez e
   recupera.
   ========================================================================== */

const VaptAiPanel = lazyWithReload(() => import('./VaptAiPanel'));

export function VaptAiLauncher() {
  const available = useVaptAiAvailable();
  const [open, setOpen] = useState(false);
  // Uma vez aberta, a gaveta fica montada — ver o comentário no JSX.
  const [everOpened, setEverOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback(() => {
    setOpen((value) => {
      if (!value) setEverOpened(true);
      return !value;
    });
  }, []);

  if (!available) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={[styles.launcher, open && styles.open].filter(Boolean).join(' ')}
        onClick={toggle}
        // Nome estável e `aria-expanded` fazendo o trabalho do estado: é o
        // padrão de botão que abre e fecha um painel, e evita dois controles
        // chamados "Fechar o Vapt IA" na tela ao mesmo tempo.
        aria-expanded={open}
        aria-label="Assistente Vapt IA"
      >
        <Sparkles size={20} strokeWidth={1.5} aria-hidden="true" />
        <span className={styles.label}>Vapt IA</span>
      </button>

      {/*
        Monta na primeira abertura e NÃO desmonta ao fechar. Desmontando, um
        turno que continuava correndo no servidor perdia a tela: o dono reabria
        e encontrava a conversa sem a resposta, com o compositor liberado, e ao
        mandar de novo levava "o Vapt IA ainda está com o pedido anterior".

        Fechada, a gaveta fica `hidden`: fora do fluxo, fora da árvore de
        acessibilidade e fora da navegação por teclado. O que sobrevive é só o
        estado — e o carregamento sob demanda continua valendo, porque nada
        monta antes da primeira abertura.
      */}
      {everOpened && (
        <Suspense fallback={null}>
          <VaptAiPanel onClose={close} returnFocusRef={buttonRef} hidden={!open} />
        </Suspense>
      )}
    </>
  );
}
